import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import StopIcon from '@mui/icons-material/Stop';
import TagIcon from '@mui/icons-material/Tag';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { MOCK_TASKS } from '../../mocks';
import { ForgeTask, ForgeTaskType } from '../../types';
import { TASK_TYPE_REGISTRY } from './TaskTypeRegistry';

type PlayerState = 'idle' | 'loading' | 'active' | 'submitting';

// ── Submit Preview Dialog ──

function formatPayloadLines(taskType: ForgeTaskType, data: any): string[] {
  if (!data) return ['No data selected'];

  switch (taskType) {
    case 'FD_CLASSIFICATION': {
      const decs = data.decisions || {};
      const counts = { confirm: 0, false_alarm: 0, reassign: 0, bad_image: 0 };
      Object.values(decs).forEach((d: any) => {
        if (d.type in counts) counts[d.type as keyof typeof counts]++;
      });
      return [
        `${Object.keys(decs).length} detection(s) classified`,
        counts.confirm > 0 ? `  Confirmed: ${counts.confirm}` : '',
        counts.false_alarm > 0 ? `  False alarm: ${counts.false_alarm}` : '',
        counts.reassign > 0 ? `  Reassigned: ${counts.reassign}` : '',
        counts.bad_image > 0 ? `  Bad image: ${counts.bad_image}` : '',
      ].filter(Boolean);
    }
    case 'UNCERTAIN_CLASSIFICATION': {
      const lines: string[] = [];
      if (data.decisions) {
        const nonFrontCount = Object.values(data.decisions).filter(
          (d: any) => d.type === 'non-front'
        ).length;
        const frontCount = Object.values(data.decisions).filter(
          (d: any) => d.type === 'front'
        ).length;
        const badImageCount = Object.values(data.decisions).filter(
          (d: any) => d.type === 'bad_image'
        ).length;
        const unknownCount = Object.values(data.decisions).filter(
          (d: any) => d.type === 'unknown_product'
        ).length;
        if (nonFrontCount) lines.push(`Non-front: ${nonFrontCount} crop(s)`);
        if (frontCount) lines.push(`Front: ${frontCount} crop(s)`);
        if (badImageCount) lines.push(`Bad image: ${badImageCount} crop(s)`);
        if (unknownCount) lines.push(`Unknown product: ${unknownCount} crop(s)`);
      }
      return lines.length > 0 ? lines : ['No decisions made'];
    }
    case 'DESIGN_CURATOR': {
      const lines: string[] = [];
      if (data.addCropIds?.length)
        lines.push(
          `Add ${data.addCropIds.length} crop(s)`,
          ...data.addCropIds.map((id: string) => `  • ${id}`)
        );
      if (data.deactivateCropIds?.length)
        lines.push(
          `Deactivate ${data.deactivateCropIds.length} crop(s)`,
          ...data.deactivateCropIds.map((id: string) => `  • ${id}`)
        );
      if (data.reassignments?.length)
        lines.push(
          `Reassign ${data.reassignments.length} crop(s)`,
          ...data.reassignments.map(
            (r: any) => `  • ${r.cropId} → ${r.targetDesignId.slice(0, 12)}`
          )
        );
      if (data.markCarton?.length) lines.push(`Mark carton: ${data.markCarton.length} crop(s)`);
      if (data.markNonFront?.length)
        lines.push(`Mark non-front: ${data.markNonFront.length} crop(s)`);
      if (data.sendToAssignQueue?.length)
        lines.push(`Send to assign queue: ${data.sendToAssignQueue.length} crop(s)`);
      return lines.length > 0 ? lines : ['No changes'];
    }
    case 'DESIGN_REVIEW': {
      const lines: string[] = [];
      if (data.merges?.length)
        lines.push(
          `Merge ${data.merges.length} design(s)`,
          ...data.merges.map(
            (m: any) => `  • ${m.sourceDesignId.slice(0, 12)} → ${m.targetDesignId.slice(0, 12)}`
          )
        );
      if (data.moves?.length)
        lines.push(
          `Move ${data.moves.length} crop(s)`,
          ...data.moves.map(
            (m: any) =>
              `  • ${m.cropId} → ${m.targetSection}${m.targetDesignId ? ` (${m.targetDesignId.slice(0, 12)})` : ''}`
          )
        );
      if (data.orientationChanges?.length)
        lines.push(
          `Re-orient ${data.orientationChanges.length} crop(s)`,
          ...data.orientationChanges.map(
            (change: any) => `  • ${change.cropId} → ${change.orientation}`
          )
        );
      if (data.deactivations?.length)
        lines.push(
          `Deactivate ${data.deactivations.length} crop(s)`,
          ...data.deactivations.map((id: string) => `  • ${id}`)
        );
      if (data.newDesigns?.length)
        lines.push(
          `Create ${data.newDesigns.length} new design(s)`,
          ...data.newDesigns.map((v: any) => `  • ${v.id} with ${v.cropIds.length} crop(s)`)
        );
      if (data.representativeChanges?.length)
        lines.push(
          `Set representative: ${data.representativeChanges.length}`,
          ...data.representativeChanges.map(
            (r: any) => `  • ${r.designId.slice(0, 12)} → crop ${r.cropId}`
          )
        );
      return lines.length > 0 ? lines : ['No changes'];
    }
    default:
      return [JSON.stringify(data, null, 2)];
  }
}

const SubmitPreviewDialog: React.FC<{
  taskType: ForgeTaskType;
  taskLabel: string;
  data: any;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ taskType, taskLabel, data, onConfirm, onCancel }) => {
  const lines = formatPayloadLines(taskType, data);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onConfirm();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onConfirm, onCancel]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: 0,
          maxWidth: 560,
          width: '90%',
          boxShadow: 'var(--shadow-elevated)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 20px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--table-header-bg)',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
            Submit — {taskLabel}
          </div>
          <CloseIcon
            onClick={onCancel}
            style={{ fontSize: 16, color: 'var(--text-tertiary)', cursor: 'pointer' }}
          />
        </div>

        {/* Payload */}
        <div
          style={{
            padding: '16px 20px',
            maxHeight: 360,
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              background: 'var(--elevated)',
              borderRadius: 'var(--radius-sm)',
              padding: '12px 14px',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              lineHeight: 1.8,
              color: 'var(--text-secondary)',
              whiteSpace: 'pre-wrap',
            }}
          >
            {lines.map((l, i) => (
              <div
                key={i}
                style={{
                  color: l.startsWith('  •') ? 'var(--text-tertiary)' : 'var(--text-primary)',
                  fontWeight: l.startsWith('  •') ? 400 : 600,
                }}
              >
                {l}
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
            padding: '12px 20px',
            borderTop: '1px solid var(--border)',
          }}
        >
          <button
            onClick={onCancel}
            style={{
              padding: '6px 16px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancel <span style={{ opacity: 0.5, fontSize: 10 }}>Esc</span>
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '6px 16px',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'var(--accent)',
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Confirm <span style={{ opacity: 0.7, fontSize: 10 }}>↵</span>
          </button>
        </div>
      </div>
    </div>
  );
};

interface SessionStats {
  completed: number;
  skipped: number;
  avgTime: number;
  streak: number;
}

export const TaskPlayerPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queueFilter = searchParams.get('queue');

  // Filter tasks by queue if a queue param is provided
  const taskPool = useMemo(() => {
    if (!queueFilter) return MOCK_TASKS;
    const filtered = MOCK_TASKS.filter((t) => t.queueName === queueFilter);
    return filtered.length > 0 ? filtered : MOCK_TASKS;
  }, [queueFilter]);

  const [state, setState] = useState<PlayerState>('idle');
  const [task, setTask] = useState<ForgeTask | null>(null);
  const [taskIndex, setTaskIndex] = useState(0);
  const [taskNumber, setTaskNumber] = useState(1);
  const [elapsed, setElapsed] = useState(0);
  const [sessionStart] = useState(() => Date.now());
  const [, setSessionElapsed] = useState(0);
  const [stats, setStats] = useState<SessionStats>({
    completed: 0,
    skipped: 0,
    avgTime: 0,
    streak: 0,
  });
  const selectionRef = useRef<any>(null);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const taskStartRef = useRef(Date.now());
  const completedTimesRef = useRef<number[]>([]);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);

  const loadTaskAtIndex = useCallback(
    (index: number) => {
      setState('loading');
      setTimeout(() => {
        const nextTask = taskPool[index % taskPool.length];
        setTask(nextTask);
        setElapsed(0);
        taskStartRef.current = Date.now();
        selectionRef.current = null;
        setState('active');
      }, 500);
    },
    [taskPool]
  );

  // Auto-fetch on mount
  useEffect(() => {
    loadTaskAtIndex(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Task timer
  useEffect(() => {
    if (state === 'active') {
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      return () => clearInterval(timerRef.current);
    }
    clearInterval(timerRef.current);
  }, [state]);

  // Session timer
  useEffect(() => {
    const id = setInterval(() => {
      setSessionElapsed(Math.floor((Date.now() - sessionStart) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sessionStart]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const requestSubmit = useCallback(() => {
    if (state !== 'active') return;
    setShowSubmitDialog(true);
  }, [state]);

  const confirmSubmit = useCallback(() => {
    setShowSubmitDialog(false);
    if (state !== 'active') return;
    const taskTime = (Date.now() - taskStartRef.current) / 1000;
    completedTimesRef.current.push(taskTime);
    const times = completedTimesRef.current;
    const avg = times.reduce((a, b) => a + b, 0) / times.length;

    setStats((prev) => ({
      completed: prev.completed + 1,
      skipped: prev.skipped,
      avgTime: avg,
      streak: prev.streak + 1,
    }));

    setState('submitting');
    setTimeout(() => {
      const nextIdx = taskIndex + 1;
      setTaskIndex(nextIdx);
      setTaskNumber((n) => n + 1);
      loadTaskAtIndex(nextIdx);
    }, 300);
  }, [state, taskIndex, loadTaskAtIndex]);

  const handleSkip = useCallback(() => {
    if (state !== 'active') return;
    setStats((prev) => ({
      ...prev,
      skipped: prev.skipped + 1,
      streak: 0,
    }));
    const nextIdx = taskIndex + 1;
    setTaskIndex(nextIdx);
    setTaskNumber((n) => n + 1);
    loadTaskAtIndex(nextIdx);
  }, [state, taskIndex, loadTaskAtIndex]);

  const handleEndSession = () => {
    navigate('/forge');
  };

  const handleSelect = (data: any) => {
    selectionRef.current = data;
  };

  // Global keyboard shortcuts
  const globalShortcuts = useMemo(
    () => ({
      enter: requestSubmit,
      s: handleSkip,
    }),
    [requestSubmit, handleSkip]
  );

  useKeyboardShortcuts(globalShortcuts, state === 'active');

  if (state === 'idle' || (state === 'loading' && !task)) {
    return <div className="forge-spinner" />;
  }

  if (state === 'loading' && task) {
    return <div className="forge-spinner" />;
  }

  if (!task) return null;

  const registry = TASK_TYPE_REGISTRY[task.type];
  const TaskComponent = registry.component;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Session stats bar */}
      <div className="session-bar">
        <div className="session-bar-left">
          <span className={`session-queue-badge task-type-${task.type.toLowerCase()}`}>
            {registry.label}
          </span>
          <div className="session-stat">
            <span className="session-stat-label">Completed:</span>
            <span className="session-stat-value">{stats.completed}</span>
          </div>
          <div className="session-stat">
            <span className="session-stat-label">Skipped:</span>
            <span className="session-stat-value">{stats.skipped}</span>
          </div>
          <div className="session-stat">
            <span className="session-stat-label">Avg:</span>
            <span className="session-stat-value">{stats.avgTime.toFixed(1)}s</span>
          </div>
          <div className="session-stat">
            <span className="session-stat-label">Streak:</span>
            <span className="session-stat-value accent">{stats.streak}</span>
          </div>
          <span className="task-chain-name">{task.chainName}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="task-header-meta">
            <TagIcon style={{ width: 14, height: 14 }} />
            {taskNumber}
          </span>
          <span className="task-timer">
            <AccessTimeIcon style={{ width: 14, height: 14 }} />
            {formatTime(elapsed)}
          </span>
          <button className="btn-secondary btn-sm" onClick={handleEndSession}>
            <StopIcon style={{ width: 14, height: 14 }} />
            End Session
          </button>
        </div>
      </div>

      {/* Task content */}
      <div className="task-player-content">
        <TaskComponent key={task.id} payload={task.payload} onSelect={handleSelect} />
      </div>

      {/* Footer */}
      <div className="task-player-footer">
        <div className="task-footer-shortcuts">
          <span className="kbd">Enter</span> Submit
          <span className="kbd">S</span> Skip
          {task.type === 'FD_CLASSIFICATION' && (
            <>
              <span className="kbd">C</span> Confirm
              <span className="kbd">W</span> Wrong
              <span className="kbd">R</span> Reassign
              <span className="kbd">B</span> Bad Image
              <span className="kbd">1-7</span> Design
              <span className="kbd">↑ ↓</span> Navigate
            </>
          )}
          {task.type === 'UNCERTAIN_CLASSIFICATION' && (
            <>
              <span className="kbd">N</span> Non-front
              <span className="kbd">F</span> Front
              <span className="kbd">B</span> Bad Image
              <span className="kbd">U</span> Unknown
              <span className="kbd">1-7</span> Design
              <span className="kbd">↑ ↓</span> Navigate
            </>
          )}
          {task.type === 'DESIGN_CURATOR' && (
            <>
              <span className="kbd">Esc</span> Clear selection
            </>
          )}
        </div>
        <div className="task-footer-actions">
          <button className="btn-secondary" onClick={handleSkip} disabled={state === 'submitting'}>
            Skip
          </button>
          <button className="btn-primary" onClick={requestSubmit} disabled={state === 'submitting'}>
            {state === 'submitting' ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>

      {/* Submit preview dialog */}
      {showSubmitDialog && task && (
        <SubmitPreviewDialog
          taskType={task.type}
          taskLabel={TASK_TYPE_REGISTRY[task.type].label}
          data={selectionRef.current}
          onConfirm={confirmSubmit}
          onCancel={() => setShowSubmitDialog(false)}
        />
      )}
    </div>
  );
};
